import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { BookOpen, Lock, Loader2, ShieldCheck, Download } from "lucide-react";
import { createCheckout } from "../lib/api";
import ShareMenu from "./ShareMenu";
import PayPalCheckout from "./PayPalCheckout";

/**
 * Featured purchase card for the Hijama Atlas Book by Sheikh Ahmed Al-Hefni.
 * - Price: £25 (pulled from backend /clinic pricing; falls back to 25 if missing)
 * - Watermarked with buyer email + token (server-side)
 * - Stripe checkout via /api/checkout/create with product_type=hijama_atlas_book
 *
 * Props:
 *   price        number  optional override (default 25)
 *   compact      bool    smaller layout for sidebars
 */
const AtlasBookCard = ({ price = 25, compact = false }) => {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const emailValid = email.trim().includes("@");

  const onBuy = async () => {
    setError("");
    if (!email.trim() || !email.includes("@")) {
      setError("Please enter a valid email — your watermarked PDF will be tied to it.");
      return;
    }
    setLoading(true);
    try {
      const data = await createCheckout({
        product_type: "hijama_atlas_book",
        email: email.trim(),
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        throw new Error("Missing checkout URL");
      }
    } catch (e) {
      setError(e?.response?.data?.detail || e.message || "Could not start checkout.");
      setLoading(false);
    }
  };

  return (
    <div
      data-testid="atlas-book-card"
      className={`relative overflow-hidden rounded-3xl bg-gradient-to-br from-forest via-forest to-sage text-sand ${
        compact ? "p-6" : "p-8 sm:p-10"
      }`}
    >
      {/* Decorative Arabic frames */}
      <div className="absolute top-3 left-4 right-4 flex justify-between text-gold/30 text-2xl font-bold pointer-events-none">
        <span>﴿</span>
        <span>﴾</span>
      </div>

      {/* Share menu (WhatsApp / Facebook / Copy link) */}
      <div className="absolute top-4 right-4 z-10">
        <ShareMenu title="Hijama Atlas Book — Sheikh Ahmed Al-Hefni" compact />
      </div>

      <div className={`grid ${compact ? "" : "md:grid-cols-[1fr_auto]"} gap-6 items-center`}>
        <div>
          <div className="inline-flex items-center gap-2 bg-gold/15 text-gold rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.2em] mb-4">
            <BookOpen className="w-3 h-3" />
            New · Featured Reference
          </div>
          <p className="arabic text-gold text-xl mb-1">أطلس الحجامة الكامل</p>
          <h3 className={`font-heading font-bold leading-tight mb-2 ${compact ? "text-2xl" : "text-3xl sm:text-4xl"}`}>
            Hijama Atlas Book
          </h3>
          <p className="text-sand/80 text-sm mb-1">
            By <strong className="text-gold">Sheikh Ahmed Al-Hefni</strong> — one of the masters of Hijama
          </p>
          <p className="text-sand/70 text-xs leading-relaxed mb-5 max-w-prose">
            The complete illustrated Atlas of Hijama points with anatomical
            diagrams, indications, and Sunnah references — the same reference our
            clinic uses for advanced practitioners and diploma students.
          </p>

          <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-sand/85 mb-6">
            <span className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-3.5 h-3.5 text-gold" />
              Watermark-protected for you
            </span>
            <span className="inline-flex items-center gap-1.5">
              <Download className="w-3.5 h-3.5 text-gold" />
              Instant download after payment
            </span>
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              data-testid="atlas-book-email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Your email for the PDF"
              className="flex-1 rounded-full bg-sand/10 border border-sand/20 px-5 py-3 text-sm text-sand placeholder-sand/50 outline-none focus:bg-sand/20 focus:border-gold/60 transition"
            />
            <button
              data-testid="atlas-book-buy"
              onClick={onBuy}
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 bg-gold text-forest hover:bg-sand rounded-full px-7 py-3 text-sm font-semibold transition-all hover:-translate-y-0.5 shadow-lg shadow-black/20 disabled:opacity-60"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Lock className="w-4 h-4" />}
              Buy for £{Number(price).toFixed(2)}
            </button>
          </div>
          {error && (
            <p data-testid="atlas-book-error" className="text-xs text-clay-200 mt-3 bg-clay/30 rounded-lg px-3 py-2">
              {error}
            </p>
          )}
          <div className="mt-3 max-w-sm">
            <PayPalCheckout
              productType="hijama_atlas_book"
              payload={{ email: email.trim() }}
              disabled={!emailValid}
              onPaid={({ order_id }) => nav(`/payment-success?session_id=${order_id}`)}
            />
          </div>
        </div>

        {!compact && (
          <div className="hidden md:block relative">
            <div className="w-44 h-60 rounded-xl bg-gradient-to-br from-gold/30 to-forest border-2 border-gold/50 shadow-2xl flex flex-col items-center justify-center text-center p-4">
              <BookOpen className="w-10 h-10 text-gold mb-3" />
              <p className="arabic text-sand text-base leading-tight">أطلس الحجامة</p>
              <p className="text-[10px] text-gold/90 mt-2 tracking-widest uppercase">Hijama Atlas</p>
              <div className="mt-4 pt-3 border-t border-gold/30 text-[9px] text-sand/70 uppercase tracking-widest">
                Sheikh Ahmed<br />Al-Hefni
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AtlasBookCard;
